import { useEffect, useState } from "react"

interface Product {
  id: string | number;
  [key: string]: unknown;
}

/**
 * Returns a filtered array of products, removing any products from the second array
 * that have an `id` already present in the first array.
 *
 * @param previousProducts - The array of products already included.
 * @param products - The new array of products to filter.
 * @returns An array of products from `products` whose `id` is not in `previousProducts`.
 */
function getUniqueProducts(previousProducts: Product[], products: Product[]) {
  const previousProductsIds = previousProducts.map(({ id }) => id);
  const seenIds = new Set(previousProductsIds);

  return products.filter(product => {
    if (seenIds.has(product.id)) {
      return false
    }

    return true
  });

}

/**
 * React hook for infinite product loading with duplicate filtering.
 * Appends only unique products to the list as new pages are loaded.
 * Resets the product list when filters change or the page is reset.
 *
 * @param products - The current array of products (e.g., from the latest fetch).
 * @param pageCount - The current page number (used for pagination).
 * @param activeFilterCount - The number of active filters (used to reset on filter change).
 * @returns The accumulated array of unique products.
 *
 * @example
 *
 * const products = fetchProducts(page, filters);
 * const allProducts = useInfiniteProducts(products, page, filters.length);
 */
const useInfiniteProducts = (
  products: Product[] = [],
  pageCount: number,
  activeFilterCount: number,
) => {
  const [allProducts, setAllProducts] = useState<Product[]>([])

  useEffect(() => {
    if (products.length > 0) {
      setAllProducts(prev => {
        const newProducts = getUniqueProducts(prev, products)

        return [...prev, ...newProducts]
      });
      return
    }

  }, [products]);

  useEffect(() => {
    if (pageCount === 1) {
      setAllProducts(products);
    }
  }, [products, pageCount, activeFilterCount]);

  return allProducts;
}

export default useInfiniteProducts
